import React, { useRef, useState } from "react";

interface InputOTPProps {
	maxLength: number;
	value: string;
	onChange: (value: string) => void;
	children: React.ReactNode;
}

interface InputOTPGroupProps {
	children: React.ReactNode;
}

interface InputOTPSlotProps {
	index: number;
}

interface InputOTPSeparatorProps {}

const InputOTPContext = React.createContext<{
	value: string;
	onChange: (value: string) => void;
	maxLength: number;
	inputRefs: React.MutableRefObject<(HTMLInputElement | null)[]>;
} | null>(null);

export const InputOTP: React.FC<InputOTPProps> = ({ maxLength, value, onChange, children }) => {
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	return (
		<InputOTPContext.Provider value={{ value, onChange, maxLength, inputRefs }}>
			<div className="flex items-center gap-2">{children}</div>
		</InputOTPContext.Provider>
	);
};

export const InputOTPGroup: React.FC<InputOTPGroupProps> = ({ children }) => {
	return <div className="flex gap-2">{children}</div>;
};

export const InputOTPSlot: React.FC<InputOTPSlotProps> = ({ index }) => {
	const context = React.useContext(InputOTPContext);
	if (!context) throw new Error("InputOTPSlot must be used within InputOTP");

	const { value, onChange, maxLength, inputRefs } = context;
	const char = value[index] || "";

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newChar = e.target.value.slice(-1);
		if (!/^\d*$/.test(newChar)) return;

		const newValue = value.split("");
		newValue[index] = newChar;
		const updatedValue = newValue.join("").slice(0, maxLength);
		onChange(updatedValue);

		if (newChar && index < maxLength - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Backspace" && !char && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
		if (e.key === "ArrowLeft" && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
		if (e.key === "ArrowRight" && index < maxLength - 1) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, maxLength);
		onChange(pastedData);
		const nextIndex = Math.min(pastedData.length, maxLength - 1);
		inputRefs.current[nextIndex]?.focus();
	};

	return (
		<input
			ref={(el) => (inputRefs.current[index] = el)}
			type="text"
			inputMode="numeric"
			maxLength={1}
			value={char}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			onPaste={handlePaste}
			className="w-12 h-12 text-center text-2xl font-semibold border-2 border-gray-300 rounded-lg focus:border-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-900/20"
		/>
	);
};

export const InputOTPSeparator: React.FC<InputOTPSeparatorProps> = () => {
	return <div className="w-3 flex items-center justify-center text-gray-400">-</div>;
};
